import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box, TableContainer, Table, TableHead, TableRow, TableCell,
    TableBody, TablePagination, Paper, CircularProgress, IconButton,
    Typography, TextField, MenuItem, Select, FormControl,
    InputLabel, Button, Grid, Chip, Tooltip
} from "@mui/material";
import {
    Visibility as VisibilityIcon,
    RestartAlt,
    SearchOff as NoDataIcon
} from "@mui/icons-material";
import { getData } from "../../../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../../../services/endpoints";

function ITDeclarationsStepper({ payPeriod }) {
    const [itTaxSummaries, setItTaxSummaries] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filter & Pagination States
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filters, setFilters] = useState({ search: "", regime: "All" });

    // Calculate Financial Year (e.g., "2025" if today is March 2026)
    const currentFinancialYear = useMemo(() => {
        const today = new Date();
        const month = today.getMonth(); // 0-11
        const year = today.getFullYear();
        return month < 3 ? (year - 1).toString() : year.toString();
    }, []);

    const fetchBulkTaxSummary = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getData(API_ENDPOINTS.getTaxSummaryByFinancialYear(currentFinancialYear));
            if (response?.statusCode === 200 || response?.result) {
                setItTaxSummaries(response.result || []);
            }
        } catch (err) {
            setItTaxSummaries([]);
        } finally {
            setLoading(false);
        }
    }, [currentFinancialYear]);

    useEffect(() => {
        fetchBulkTaxSummary();
    }, [fetchBulkTaxSummary]);

    // --- Filter Logic ---
    const filteredData = useMemo(() => {
        return itTaxSummaries.filter((emp) => {
            const matchesSearch =
                emp.employeeCode?.toLowerCase().includes(filters.search.toLowerCase()) ||
                emp.employeeName?.toLowerCase().includes(filters.search.toLowerCase());

            const matchesRegime =
                filters.regime === "All" ||
                emp.regime === filters.regime;

            return matchesSearch && matchesRegime;
        });
    }, [itTaxSummaries, filters]);

    const handleReset = () => {
        setFilters({ search: "", regime: "All" });
        setPage(0);
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            {/* Filter Header */}
            <Grid container spacing={2} mb={3} alignItems="center">
                <Grid size={{ xs: 12, sm: 6, md: 8 }}>
                    <TextField
                        fullWidth size="small" label="Search Employee"
                        placeholder="Search by ID or Name"
                        value={filters.search}
                        onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(0); }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Regime</InputLabel>
                        <Select
                            label="Regime"
                            value={filters.regime}
                            onChange={(e) => { setFilters({ ...filters, regime: e.target.value }); setPage(0); }}
                        >
                            <MenuItem value="All">All</MenuItem>
                            <MenuItem value="Old Regime">Old Regime</MenuItem>
                            <MenuItem value="New Regime">New Regime</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 2, md: 1 }}>
                    <Button fullWidth startIcon={<RestartAlt />} onClick={handleReset} sx={{ textTransform: 'none', color: 'text.secondary' }}>Reset</Button>
                </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #E2E8F0', minHeight: filteredData.length === 0 ? '300px' : 'auto' }}>
                {filteredData.length > 0 ? (
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                            <TableRow>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Employee ID</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Name</TableCell>
                                <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Gross Annual</TableCell>
                                <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Taxable Income</TableCell>
                                <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Base Tax</TableCell>
                                <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Cess</TableCell>
                                <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Annual Tax</TableCell>
                                <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Regime</TableCell>
                                <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ bgcolor: '#FFFFFF' }}>
                            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                <TableRow key={row.employeePersonalId} hover sx={{
                                    '&:hover': {
                                        bgcolor: '#F1F5F9 !important'
                                    }
                                }}>
                                    <TableCell sx={{ fontWeight: 600 }}>{row.employeeCode}</TableCell>
                                    <TableCell>{row.employeeName}</TableCell>
                                    <TableCell align="center">₹{Number(row.grossAnnual || 0).toLocaleString()}</TableCell>
                                    <TableCell align="center">₹{Number(row.taxableIncome || 0).toLocaleString()}</TableCell>
                                    <TableCell align="center">₹{Number(row.baseTax || 0).toLocaleString()}</TableCell>
                                    <TableCell align="center">₹{Number(row.cess || 0).toLocaleString()}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: row.annualTax > 0 ? 'error.main' : 'inherit' }}>
                                        ₹{Number(row.annualTax || 0).toLocaleString()}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={row.regime}
                                            size="small"
                                            color={row.regime === "New Regime" ? "primary" : "secondary"}
                                            variant="outlined"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="View Declaration">
                                            <IconButton size="small" color="primary"
                                                onClick={() => {
                                                    // Store the name to search for and a flag to trigger the tab change
                                                    localStorage.setItem("itDeclarationSearchQuery", row.employeeName);
                                                    localStorage.setItem("openPayrollTab", "IT Declarations");

                                                    // If you have access to a 'setTabValue' via props you'd call it, 
                                                    // otherwise, a page reload or a state lift-up is needed.
                                                    // For this architecture, we trigger a refresh or use the storage listener.
                                                    window.dispatchEvent(new Event("storage"));
                                                    // In many SPA setups, you might need to navigate or force a re-render
                                                    window.location.reload(); // Simple approach to catch the localStorage in parent
                                                }}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10 }}>
                        <NoDataIcon sx={{ fontSize: 60, color: '#CBD5E1', mb: 2 }} />
                        <Typography color="textSecondary" variant="h6">No Tax Records Found</Typography>
                        <Typography color="textSecondary" variant="body2">Try adjusting your filters or search terms</Typography>
                    </Box>
                )}
            </TableContainer>

            <TablePagination
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25]}
            />
        </Box>
    );
}

export default ITDeclarationsStepper;